"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaCarouselType } from "embla-carousel";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";

interface FeatureShowcaseProps {
  className?: string;
}

const showcaseImages: { src: string; alt: string }[] = [
  {
    src: "/banner/1.png",
    alt: "數據報表生成",
  },
  {
    src: "/banner/2.png",
    alt: "Google Sheets 串接",
  },
  {
    src: "/banner/3.png",
    alt: "CLOUD MCP 分析",
  },
  {
    src: "/banner/4.png",
    alt: "數據監測系統",
  },
  {
    src: "/banner/5.png",
    alt: "競品素材下載",
  },
  {
    src: "/banner/6.png",
    alt: "Threads 海巡",
  },
  {
    src: "/banner/7.png",
    alt: "Instagram 貼文下載",
  },
  {
    src: "/banner/8.png",
    alt: "BigQuery 數據倉儲",
  },
  {
    src: "/banner/9.png",
    alt: "META 廣告受眾查詢",
  },  {
    src: "/banner/10.png",
    alt: "META 操作紀錄",
  },
];

const TWEEN_FACTOR_BASE = 0.4;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export default function FeatureShowcase({ className }: FeatureShowcaseProps) {
  const dict = useDictionary();
  const prefersReducedMotion = useReducedMotion();

  const cards = dict.featureShowcase.cards.map((card, index) => ({
    ...card,
    ...showcaseImages[index % showcaseImages.length],
  }));

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
  });

  const tweenNodes = useRef<HTMLElement[]>([]);
  const tweenFactor = useRef(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const setTweenNodes = useCallback((api: EmblaCarouselType) => {
    tweenNodes.current = api.slideNodes().map((node) => {
      return node.querySelector("[data-tween]") as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((api: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  // Continuous "Apple-style" slide effect: the centered card sits at full
  // scale/opacity while neighbours gently shrink and fade as you drag.
  const tweenStyles = useCallback(
    (api: EmblaCarouselType, eventName?: string) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();
              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);
                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tween = clamp(
            1 - Math.abs(diffToTarget * tweenFactor.current),
            0,
            1,
          );
          const node = tweenNodes.current[slideIndex];
          if (!node) return;
          node.style.transform = `scale(${(0.82 + tween * 0.18).toFixed(3)})`;
          node.style.opacity = (0.35 + tween * 0.65).toFixed(3);
        });
      });
    },
    [],
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const onReInit = () => {
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);
      tweenStyles(emblaApi);
      onSelect();
    };

    // Ref + DOM setup only (no setState in the effect body).
    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenStyles(emblaApi);

    emblaApi
      .on("select", onSelect)
      .on("reInit", onReInit)
      .on("scroll", tweenStyles)
      .on("slideFocus", tweenStyles);

    return () => {
      emblaApi
        .off("select", onSelect)
        .off("reInit", onReInit)
        .off("scroll", tweenStyles)
        .off("slideFocus", tweenStyles);
    };
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenStyles]);

  // Gentle autoplay that pauses while the tab is hidden or the user interacts.
  useEffect(() => {
    if (!emblaApi || prefersReducedMotion) return;

    let paused = false;
    const pause = () => (paused = true);
    const resume = () => (paused = false);

    emblaApi.on("pointerDown", pause).on("pointerUp", resume);

    const timer = setInterval(() => {
      if (paused) return;
      if (document.visibilityState !== "visible") return;
      emblaApi.scrollNext();
    }, 4500);

    return () => {
      clearInterval(timer);
      emblaApi.off("pointerDown", pause).off("pointerUp", resume);
    };
  }, [emblaApi, prefersReducedMotion]);

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-[radial-gradient(circle_at_top_right,_rgba(125,211,252,0.30),_transparent_40%),linear-gradient(180deg,#f0f7ff_0%,#dcecff_50%,#f0f7ff_100%)] py-20",
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mb-2 max-w-3xl text-center"
        >
          <h2 className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)]">
            <motion.span className="hero-ink text-6xl font-black sm:text-7xl lg:text-7xl">
              {cards.length}
            </motion.span>
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
              {dict.featureShowcase.headingUnit}
            </span>
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
              {dict.featureShowcase.heading}
            </span>
          </h2>
        </motion.div>

        <div className="relative">
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/70 text-[var(--blue-700)] shadow-[0_12px_30px_rgba(56,132,255,0.18)] backdrop-blur-md transition-all hover:scale-105 hover:bg-white sm:flex lg:-translate-x-4"
            aria-label="上一張功能"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/70 text-[var(--blue-700)] shadow-[0_12px_30px_rgba(56,132,255,0.18)] backdrop-blur-md transition-all hover:scale-105 hover:bg-white sm:flex lg:translate-x-4"
            aria-label="下一張功能"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Carousel viewport */}
          <div
            className="overflow-hidden py-6 [mask-image:linear-gradient(90deg,transparent,#000_5%,#000_87%,transparent)]"
            ref={emblaRef}
          >
            <div className="flex touch-pan-y">
              {cards.map((card, index) => (
                <div
                  key={`${card.title}-${index}`}
                  className="min-w-0 flex-[0_0_75%] px-3 sm:flex-[0_0_60%] sm:px-4 lg:flex-[0_0_46%]"
                >
                  <article
                    data-tween
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.92)_0%,rgba(232,242,255,0.85)_100%)] shadow-[0_30px_70px_rgba(56,132,255,0.16)] backdrop-blur-xl will-change-transform"
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-[linear-gradient(135deg,#1e3a8a,#2563eb)]">
                      <Image
                        src={card.src}
                        alt={card.alt}
                        fill
                        sizes="(max-width: 640px) 86vw, (max-width: 1024px) 60vw, 46vw"
                        className="object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                        draggable={false}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--blue-900)]/15 to-transparent" />
                    </div>

                    {/* Text */}
                    <div className="flex flex-1 flex-col gap-2 p-6 sm:p-7">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--blue-500)]">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3 className="text-xl font-bold text-[var(--blue-900)] sm:text-2xl">
                        {card.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-[var(--blue-900)]/65 sm:text-base">
                        {card.description}
                      </p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center gap-2.5">
            {cards.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                aria-label={`跳到第 ${index + 1} 張功能`}
                aria-current={index === selectedIndex}
                style={
                  {
                    width: index === selectedIndex ? "1.75rem" : "0.5rem",
                  } as CSSProperties
                }
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  index === selectedIndex
                    ? "bg-[var(--blue-700)]"
                    : "bg-[var(--blue-300)] hover:bg-[var(--blue-500)]",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
